import React, { useEffect, useMemo, useState } from 'react';

import useTreatmentPlanMasterStore from '../../../stores/treatmentPlanMasterStore';
import useProcedureStore from '../../../stores/procedureStore';

/**
 * SavedCombosPicker — Phase 3.0 in-disc replacement for AttachPlanModal's
 * combo-picker step. The doctor picks a saved TreatmentPlan master from
 * a list; the disc attaches it to the current visit with sensible
 * defaults (per-step master defaults; on MISSING_ASSIGNEES we offer
 * "Default all to me" inline).
 *
 * Caveats vs the full AttachPlanModal:
 *   - No per-step exclusion UI — defaults to including every step.
 *     Optional steps can still be excluded after attach via the existing
 *     "Exclude (optional)" chip action.
 *   - No per-step assignee picker — falls back to master defaults; if
 *     team validation fails, "Default all to me" pre-fills the caller.
 *   - Anything more nuanced (per-step assignees, step exclusions) lives
 *     behind ⤢ canvas (Step 10+) or the legacy AttachPlanModal route.
 *
 * Props:
 *   visit              — { visit_id, patient_id }
 *   viewer             — { memberId, role }
 *   onAttached()       — fires after a successful attach
 *   palette            — disc theme tokens
 *   onClose()          — closes the picker subview
 */
export default function SavedCombosPicker({ visit, viewer, onAttached, palette, onClose }) {
  const plans = useTreatmentPlanMasterStore((s) => s.plans);
  const isLoading = useTreatmentPlanMasterStore((s) => s.isLoading);
  const fetchPlans = useTreatmentPlanMasterStore((s) => s.fetchPlans);
  const attachToVisit = useTreatmentPlanMasterStore((s) => s.attachToVisit);

  const procedures = useProcedureStore((s) => s.procedures);
  const fetchProcedures = useProcedureStore((s) => s.fetchProcedures);

  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [missingPayload, setMissingPayload] = useState(null);
  const [recoveryPlanId, setRecoveryPlanId] = useState(null);
  // Tap-to-preview: expanded plan id (steps shown inline) so doctors
  // can verify what's inside before attaching to a patient.
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!plans || plans.length === 0) {
      fetchPlans().catch(() => {});
    }
  }, [plans, fetchPlans]);

  useEffect(() => {
    if (!procedures || procedures.length === 0) {
      fetchProcedures().catch(() => {});
    }
  }, [procedures, fetchProcedures]);

  const procById = useMemo(() => {
    const map = {};
    for (const p of procedures || []) map[p.procedure_id] = p;
    return map;
  }, [procedures]);

  const visiblePlans = useMemo(() => {
    return (plans || []).filter((p) => p.is_active !== false);
  }, [plans]);

  const handleAttach = async (plan, { defaultAllToMe = false } = {}) => {
    if (!plan?.plan_id || busyId) return;
    setBusyId(plan.plan_id);
    setError('');

    // Default every step to today so sittings get pre-materialised on
    // attach. Without `step_dates`, the backend only creates PlanItem
    // rows — no sittings — and the disc has nothing to render. Doctors
    // can still pick per-step dates via the full editor.
    const today = new Date().toISOString().slice(0, 10);
    const stepDates = (plan.steps || []).reduce((acc, step) => {
      if (step.step_id) acc[step.step_id] = today;
      return acc;
    }, {});

    // Strict-ordering plans default to upfront payment so reception
    // collects once and every step thereafter is just sequence-gated.
    // Flexible plans keep the per-step default — receptionist collects
    // before each step's execution. Backend has the same default
    // (patient_plan_service.attach_plan_to_visit) but sending it
    // explicitly keeps the contract obvious to anyone reading the
    // network tab.
    const isStrict = (plan.ordering_rule || 'flexible') === 'strict';
    const body = {
      patient_id: visit.patient_id,
      source_visit_id: visit.visit_id,
      excluded_step_ids: [],
      step_dates: stepDates,
      payment_mode: isStrict ? 'upfront' : 'per_step',
    };
    if (defaultAllToMe) {
      body.assignee_resolutions = (plan.steps || []).reduce((acc, step) => {
        acc[step.step_id] = { executor_member_id: viewer?.memberId };
        return acc;
      }, {});
    }

    const result = await attachToVisit(plan.plan_id, body);
    setBusyId(null);

    if (result?.success) {
      setMissingPayload(null);
      setRecoveryPlanId(null);
      if (typeof onAttached === 'function') await onAttached(result);
      return;
    }

    if (result?.errorCode === 'MISSING_ASSIGNEES') {
      setMissingPayload(result.errorPayload || null);
      setRecoveryPlanId(plan.plan_id);
      setError(result.error || 'Some steps need an assignee.');
    } else {
      setError(result?.error || 'Could not attach plan.');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <strong style={{ flex: 1, fontSize: '0.8125rem' }}>📘 Treatment plans</strong>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close treatment plans"
          className="px-2 py-0.5 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                     dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
          style={{ fontSize: '0.6875rem', borderColor: palette.border }}
        >
          ✕
        </button>
      </header>

      {isLoading && (
        <p style={{ margin: 0, fontSize: '0.6875rem', opacity: 0.7 }}>
          Loading plans…
        </p>
      )}

      {!isLoading && visiblePlans.length === 0 && (
        <p style={{ margin: 0, fontSize: '0.6875rem', opacity: 0.7 }}>
          No treatment plans yet. Use ❤ to save your current procedures as a reusable plan.
        </p>
      )}

      {error && (
        <p style={{ margin: 0, fontSize: '0.6875rem', color: '#b91c1c' }}>
          {error}
        </p>
      )}

      <ul role="listbox" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {visiblePlans.map((plan) => {
          const isRecovery = recoveryPlanId === plan.plan_id && missingPayload;
          const steps = plan.steps || [];
          const stepCount = steps.length;
          const isExpanded = expandedId === plan.plan_id;
          const totalCost = steps.reduce((sum, step) => {
            const proc = procById[step.procedure_id];
            const stepCostRaw = step.estimated_cost_override ?? proc?.default_cost ?? 0;
            const stepCost = Number(stepCostRaw) || 0;
            const stepDiscount = Number(step.discount_amount || 0) || 0;
            return sum + Math.max(0, stepCost - stepDiscount);
          }, 0);
          return (
            <li key={plan.plan_id}>
              <div
                className={`rounded-md border ${isRecovery
                  ? 'border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30'
                  : ''}`}
                style={{
                  padding: '0.5rem 0.625rem',
                  background: isRecovery
                    ? undefined
                    : palette.bgAlt,
                  border: isRecovery
                    ? undefined
                    : `1px solid ${palette.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.375rem',
                }}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : plan.plan_id)}
                  aria-expanded={isExpanded}
                  className="text-left"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: palette.text,
                    cursor: 'pointer',
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: '0.6875rem', opacity: 0.6 }}>
                    {isExpanded ? '▾' : '▸'}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {plan.plan_name}
                  </span>
                  <span style={{ fontSize: '0.625rem', opacity: 0.7 }}>
                    {stepCount} step{stepCount === 1 ? '' : 's'}
                    {totalCost > 0 ? ` · ₹${Math.round(totalCost).toLocaleString('en-IN')}` : ''}
                  </span>
                </button>
                {isExpanded && stepCount > 0 && (
                  <ol style={{ margin: 0, padding: '0 0 0 1rem', fontSize: '0.6875rem', opacity: 0.85, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {steps.map((step, idx) => {
                      const proc = procById[step.procedure_id];
                      const name = proc?.name || step.procedure_id;
                      return (
                        <li key={step.step_id || `${plan.plan_id}-${idx}`}>
                          <span>{name}</span>
                          {step.is_mandatory === false && (
                            <span style={{ marginLeft: 4, opacity: 0.7 }}>(optional)</span>
                          )}
                          {step.requires_approval && (
                            <span style={{ marginLeft: 4, opacity: 0.7 }}>· needs approval</span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
                <button
                  type="button"
                  onClick={() => handleAttach(plan)}
                  disabled={busyId === plan.plan_id}
                  className="rounded-md bg-teal-600 text-white text-xs font-semibold py-1
                             hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {busyId === plan.plan_id ? 'Attaching…' : 'Attach to this visit'}
                </button>
                {isRecovery && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <p style={{ margin: 0, fontSize: '0.6875rem', color: '#b91c1c' }}>
                      Some steps need an assignee. Click below to default all to you.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleAttach(plan, { defaultAllToMe: true })}
                      disabled={busyId === plan.plan_id}
                      className="rounded-md bg-rose-600 text-white text-xs font-semibold py-1
                                 hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Default all to me
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
